import React from "react";
import {
  Box,
  Heading,
  Text,
  Image,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";

// ✅ Imported Components
import HeroSection from "../components/HeroSection.jsx";
import AboutSection from "../components/AboutSection.jsx";
import TherapySection from "../components/TherapySection.jsx";
import ConditionsSection from "../components/ConditionsSection.jsx";
import MeetPractitioner from "../components/MeetPractitioner.jsx";
import TestimonialsSection from "../components/TestimonialsSection.jsx";
import BrandsSection from "../components/BrandsSection.jsx";
import ServicesSection from "../components/ServicesSection.jsx";
import BrandShowcase from "../components/BrandShowcase.jsx";
import ContactSection from "../components/ContactSection.jsx";
import FeatureShowcase from "../components/FeatureShowcase.jsx";
import LetsWorkTogether from "../components/LetsWorkTogether.jsx";
import Footer2 from "../components/Footer2.jsx";

/**
 * 🎴 ServiceCard Component
 * Reusable card for displaying interior design categories / services
 */
const ServiceCard = ({ service }) => (
  <Box
    borderWidth="1px"
    borderRadius="xl"
    overflow="hidden"
    boxShadow="lg"
    bg={useColorModeValue("white", "gray.700")}
    transition="all 0.3s ease"
    _hover={{ transform: "translateY(-5px)", boxShadow: "xl" }}
  >
    {/* Service / Project Image */}
    <Image
      src={service.image}
      alt={service.title}
      objectFit="cover"
      height="200px"
      width="100%"
    />

    {/* Service Content */}
    <Box p={5}>
      <Heading size="md" mb={2}>
        {service.title}
      </Heading>
      <Text fontSize="sm" color="gray.600">
        {service.shortDescription}
      </Text>

      {/* CTA Button */}
      <Button
        as={RouterLink}
        to={service.path}
        mt={4}
        size="sm"
        colorScheme="teal"
      >
        Learn More
      </Button>
    </Box>
  </Box>
);

/**
 * 🏠 Home Page Component
 * Main landing page for Pentagon Premium Interior Design & Home Improvement Store
 */
const Home = () => {
  return (
    <Box>
      {/* 🧠 SEO + Metadata */}
      <Helmet>
        <title>Pentagon – Premium Interior Design & Home Improvement</title>
        <meta
          name="description"
          content="Pentagon is a premium home improvement and interior design brand in Nashik, Maharashtra. We offer luxury interiors, modern architecture solutions, and tailored home improvement services."
        />

        {/* 🏢 Organization Schema Markup */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "HomeAndConstructionBusiness",
            "name": "Pentagon Store",
            "url": "https://pentagonindia.in/",
            "logo": "https://pentagonindia.in/logo.png",
            "image": "https://pentagonindia.in/storefront.jpg",
            "description": "Premium home improvement and interior design store in Nashik, Maharashtra.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "16/1/P, near ESDS circle, MIDC, Satpur Colony",
              "addressLocality": "Nashik",
              "addressRegion": "MH",
              "postalCode": "422007",
              "addressCountry": "IN"
            },
            "telephone": "+91-7378511300",
            "priceRange": "$$",
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday"
                ],
                "opens": "09:00",
                "closes": "20:00"
              }
            ],
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 20.005,
              "longitude": 73.779
            },
            "sameAs": [
              "https://www.google.com/maps/place/Pentagon+Store"
            ],
            "serviceType": ["Assembly service", "Same-day delivery"]
          }
        `}</script>

        {/* ❓ FAQ Schema Markup */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Where is Pentagon Store located?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Pentagon Store is located at 16/1/P, near ESDS circle, MIDC, Satpur Colony, Nashik, Maharashtra 422007."
                }
              },
              {
                "@type": "Question",
                "name": "What services does Pentagon Store provide?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Pentagon Store offers premium interior design services, home improvement products, assembly service, and same-day delivery."
                }
              },
              {
                "@type": "Question",
                "name": "What are Pentagon Store's working hours?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Pentagon Store is open Monday to Saturday, from 9:00 AM to 8:00 PM."
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      {/* 🔷 Hero Section */}
      <HeroSection />

      {/* 🏢 Brand Showcase */}
      <BrandShowcase maxW="1200px" mx="auto" />

      {/* 📞 Contact Section */}
      <ContactSection maxW="1200px" mx="auto" />

      {/* 🌟 Features Section */}
      <FeatureShowcase maxW="1200px" mx="auto" />

      {/* 🧾 Design & Improvement Services Overview (Optional - currently hidden) */}
      {/* <ServicesSection /> */}

      {/* 🗣️ Client Testimonials */}
      <TestimonialsSection />

      {/* 🤝 Call To Action */}
      <LetsWorkTogether />

      {/* ⚓ Footer */}
      <Footer2 />
    </Box>
  );
};

export default Home;
